import type {
  TenderCrawlRunDto,
  TenderDto,
  TenderListDto,
  TenderStatus,
  TenderWatchProfileDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, query } from '../../lib/api.js';

export interface TenderFilters {
  sort?: 'relevance' | 'deadline' | 'published';
  status?: TenderStatus;
  productArea?: string;
  search?: string;
  includeBelowThreshold?: boolean;
  includeClosed?: boolean;
}

/** The anbud list, sorted server-side by relevance or deadline. */
export function useTenders(filters: TenderFilters) {
  return useQuery({
    queryKey: ['tenders', filters],
    queryFn: () => api.get<TenderListDto>(`/sales/tenders${query({ ...filters })}`),
  });
}

export function useTender(id: string | null) {
  return useQuery({
    queryKey: ['tender', id],
    queryFn: () => api.get<TenderDto>(`/sales/tenders/${id}`),
    enabled: Boolean(id),
  });
}

function invalidateTenders(queryClient: ReturnType<typeof useQueryClient>, id?: string) {
  void queryClient.invalidateQueries({ queryKey: ['tenders'] });
  if (id) void queryClient.invalidateQueries({ queryKey: ['tender', id] });
}

export function useUpdateTenderStatus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { status: TenderStatus; noBidReason?: string | null; noBidNote?: string | null }) =>
      api.patch<TenderDto>(`/sales/tenders/${id}/status`, body),
    onSuccess: () => invalidateTenders(queryClient, id),
  });
}

export function useAssignTender(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ownerUserId: string | null) =>
      api.patch<TenderDto>(`/sales/tenders/${id}/assign`, { ownerUserId }),
    onSuccess: () => invalidateTenders(queryClient, id),
  });
}

// ── Overvåkningsprofiler ──────────────────────────────────────────────────────

export function useWatchProfiles() {
  return useQuery({
    queryKey: ['tender-profiles'],
    queryFn: () => api.get<TenderWatchProfileDto[]>('/sales/profiles'),
  });
}

export interface WatchProfileInput {
  name: string;
  productArea: string;
  description?: string | null;
  cpvCodes: string[];
  includeKeywords: string[];
  excludeKeywords: string[];
  buyerTypes: string[];
  noticeTypes: string[];
  notifyUserIds: string[];
  minRelevance: number;
  isActive: boolean;
}

export function useSaveWatchProfile(id?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: WatchProfileInput) =>
      id
        ? api.patch<TenderWatchProfileDto>(`/sales/profiles/${id}`, body)
        : api.post<TenderWatchProfileDto>('/sales/profiles', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tender-profiles'] });
      void queryClient.invalidateQueries({ queryKey: ['tenders'] });
    },
  });
}

export function useDeleteWatchProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/sales/profiles/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tender-profiles'] }),
  });
}

// ── Kjøringer ─────────────────────────────────────────────────────────────────

export function useCrawlRuns(take = 5) {
  return useQuery({
    queryKey: ['tender-crawl-runs', take],
    queryFn: () => api.get<TenderCrawlRunDto[]>(`/sales/crawl-runs?take=${take}`),
  });
}
